import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Zap, Phone, MessageCircle, ShieldCheck, Clock, Award, Users, Wrench, Cpu, Layers, BatteryCharging, ArrowRight, CheckCircle2, ChevronRight, Cctv, Tv, Flame, Refrigerator, Wifi, Sparkles, RadioTower, BellRing, Radio, PlugZap } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useQuoteModal } from "@/components/QuoteModal";
import { Reveal } from "@/components/Reveal";
import { SITE, IMAGES } from "@/data/site";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVICE_ICONS = { electrical: Zap, automation: Cpu, panels: Layers, maintenance: Wrench, power: BatteryCharging };
const HOME_ICONS = { cctv: Cctv, tv: Tv, geyser: Flame, fridge: Refrigerator, wifi: Wifi, smart: Sparkles, bridge: RadioTower, intercom: BellRing, walkie: Radio, dg: PlugZap };
const WHY_ICONS = [ShieldCheck, Clock, Award, Users];

function Stat({ value, suffix, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1300;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return (
    <div ref={ref} className="text-center sm:text-left">
      <div className="font-mono text-4xl sm:text-5xl font-bold text-electric tracking-tight" data-testid="stat-value">
        {n}{suffix}
      </div>
      <div className="mt-2 text-sm font-medium text-slate-500">{label}</div>
    </div>
  );
}

export default function Home() {
  const { t } = useLang();
  const { open } = useQuoteModal();

  return (
    <div>
      {/* ---------------- HERO ---------------- */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-pattern-dark opacity-60" />
        <div className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-electric/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-royal/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full glass-dark px-4 py-1.5 text-xs font-mono uppercase tracking-[0.2em] text-sky-300 mb-6">
              <Zap className="w-3.5 h-3.5 text-amber2" fill="currentColor" />
              {t.hero.eyebrow}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none md:leading-tight" data-testid="hero-title">
              {t.hero.titleA}<br />
              <span className="text-sky-300">{t.hero.titleB}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="hero-promise">
              {t.hero.promise}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={open}
                data-testid="hero-quote-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-electric px-8 py-3.5 text-sm sm:text-base font-semibold text-white hover:bg-blue-500 active:scale-[0.98] transition-all duration-200 shadow-card-hover"
              >
                {t.hero.ctaQuote}
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={SITE.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-wa px-8 py-3.5 text-sm sm:text-base font-semibold text-white hover:bg-[#1fb855] active:scale-[0.98] transition-all duration-200"
              >
                <MessageCircle className="w-5 h-5" />
                {t.hero.ctaWhatsapp}
              </a>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {t.hero.badges.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-amber2" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src={IMAGES.hero}
                alt="Electrical panel testing"
                className="w-full h-72 sm:h-96 lg:h-[440px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-4 sm:left-6 glass-dark rounded-2xl px-5 py-4 shadow-card-hover" data-testid="hero-stat-card">
              <div className="font-mono text-2xl font-bold text-amber2">{t.hero.statCard.value}</div>
              <div className="text-xs text-slate-300 mt-0.5">{t.hero.statCard.label}</div>
            </div>
            <div className="absolute -top-4 right-4 glass rounded-2xl px-5 py-4 shadow-card-hover hidden sm:block" data-testid="hero-project-card">
              <div className="font-mono text-2xl font-bold text-navy">{t.hero.projectCard.value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{t.hero.projectCard.label}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TRUST STATS ---------------- */}
      <section className="py-14 sm:py-16 border-b border-slate-100" data-testid="trust-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-testid="trust-stats-grid">
            {t.stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <Stat value={s.value} suffix={s.suffix} label={s.label} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- ABOUT ---------------- */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.electrician_panel}
                alt="Nextron technician at work"
                loading="lazy"
                className="w-full h-80 sm:h-96 object-cover rounded-3xl shadow-card"
              />
              <div className="absolute -bottom-5 right-6 glass rounded-2xl px-5 py-4 shadow-card-hover">
                <div className="font-mono text-2xl font-bold text-navy">
                  12<span className="text-electric">mo</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{t.about.warrantyBadge}</div>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.about.eyebrow}</p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.about.title}</h2>
              <p className="mt-4 text-base text-slate-500 leading-relaxed">{t.about.body}</p>
            </Reveal>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {t.about.points.map((p, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3.5">
                    <CheckCircle2 className="w-5 h-5 text-electric shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700 font-medium">{p}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <button
                onClick={open}
                data-testid="about-quote-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-royal active:scale-[0.98] transition-all duration-200"
              >
                {t.hero.ctaQuote}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-50 grid-pattern-light" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.steps.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy max-w-2xl">{t.steps.title}</h2>
            <p className="mt-3 text-base text-slate-500 max-w-xl">{t.steps.subtitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {t.steps.items.map((step, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div
                  className="relative h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
                  data-testid={`how-it-works-step-${i + 1}`}
                >
                  <span className="font-mono text-5xl font-bold text-blue-100">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-navy">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{step.desc}</p>
                  {i < 2 && <ChevronRight className="hidden md:block absolute top-1/2 -right-5 w-5 h-5 text-blue-300" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- SERVICES TEASER ---------------- */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="services-teaser-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.servicesTeaser.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.servicesTeaser.title}</h2>
            <p className="mt-3 text-base text-slate-500 max-w-xl">{t.servicesTeaser.subtitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.servicesPage.items.map((s, i) => {
              const Icon = SERVICE_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 0.08} className="h-full">
                  <Link
                    to="/services"
                    className="flex flex-col h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group"
                  >
                    <span className="w-12 h-12 rounded-xl bg-blue-50 text-electric flex items-center justify-center group-hover:bg-electric group-hover:text-white transition-colors duration-300">
                      <Icon className="w-6 h-6" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-semibold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-2">{s.desc}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-electric">
                      {t.nav.services}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
            <Reveal delay={0.4} className="h-full">
              <div className="flex flex-col h-full rounded-2xl bg-navy p-7 text-white">
                <h3 className="font-display text-lg font-semibold">{t.servicesTeaser.customTitle}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t.servicesTeaser.customDesc}</p>
                <a
                  href={SITE.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-custom-whatsapp"
                  className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber2 hover:text-amber-400 transition-colors duration-200"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t.servicesTeaser.customCta}
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal>
            <Link to="/services" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-all duration-200" data-testid="home-view-all-services">
              {t.servicesTeaser.viewAll}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------------- HOME SERVICES STRIP ---------------- */}
      <section className="py-14 sm:py-16 bg-slate-50 border-y border-slate-100" data-testid="home-services-strip-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.homeServices.eyebrow}</p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-navy">{t.homeServices.title}</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-all duration-200" data-testid="home-services-strip-view-all">
              {t.homeServices.viewAll}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="home-services-strip-grid">
            {t.homeServices.items.concat(t.extraServices.items).map((s, i) => {
              const Icon = HOME_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 0.05}>
                  <Link
                    to="/services"
                    className="flex items-center gap-3 h-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300"
                    data-testid={`home-service-strip-${s.id}`}
                  >
                    <span className="w-9 h-9 rounded-lg bg-blue-50 text-electric flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="text-xs font-semibold text-navy leading-tight">{s.title}</span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- GALLERY STRIP ---------------- */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="gallery-strip-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.gallery.eyebrow}</p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.gallery.title}</h2>
            </div>
            <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-all duration-200" data-testid="gallery-view-all">
              {t.gallery.viewAll}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gallery-strip-grid">
            {t.gallery.items.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group relative overflow-hidden rounded-2xl aspect-square">
                  <img
                    src={IMAGES[g.img]}
                    alt={g.caption}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/10 to-transparent" />
                  <p className="absolute bottom-0 left-0 right-0 p-4 text-xs sm:text-sm font-medium text-white leading-snug">{g.caption}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- WHY CHOOSE US ---------------- */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-50" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.why.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.why.title}</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="why-choose-us-grid">
            {t.why.items.map((item, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08} className="h-full">
                  <div className="h-full rounded-2xl border-t-2 border-amber2/0 hover:border-amber2 bg-white p-7 hover:shadow-card transition-all duration-300">
                    <span className="w-12 h-12 rounded-xl bg-navy text-amber2 flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-semibold text-navy">{item.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-electric font-semibold">{t.faq.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{t.faq.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
              {t.faq.items.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-slate-200 bg-white px-6 shadow-card data-[state=open]:shadow-card-hover transition-shadow duration-300">
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-navy hover:text-electric hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-500 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 grid-pattern-dark opacity-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{t.finalCta.title}</h2>
            <p className="mt-4 text-base sm:text-lg text-slate-300">{t.finalCta.subtitle}</p>
            <div className="mt-9 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <a
                href={SITE.phoneHref}
                data-testid="home-final-cta-call"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy px-8 py-3.5 font-semibold hover:bg-slate-100 active:scale-[0.98] transition-all duration-200"
              >
                <Phone className="w-5 h-5" />
                {t.finalCta.call}
              </a>
              <a
                href={SITE.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-final-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-wa px-8 py-3.5 font-semibold hover:bg-[#1fb855] active:scale-[0.98] transition-all duration-200"
              >
                <MessageCircle className="w-5 h-5" />
                {t.finalCta.whatsapp}
              </a>
              <button
                onClick={open}
                data-testid="home-final-cta-quote"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-3.5 font-semibold text-white hover:bg-white/10 active:scale-[0.98] transition-all duration-200"
              >
                {t.finalCta.quote}
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
